import { useEffect, useState } from "react";

const FULLSCREEN_QUERY = "(display-mode: fullscreen)";

const readIsFullscreen = () =>
  window.matchMedia?.(FULLSCREEN_QUERY).matches ?? false;

/**
 * True while the browser itself is in full-screen mode (F11, or Ctrl+Cmd+F on
 * macOS).
 *
 * Deliberately not `document.fullscreenElement`: that only tracks the
 * Fullscreen API and stays null for browser-chrome full-screen. Installed PWAs
 * are not false positives either, because `public/manifest.json` declares
 * `display: standalone`, which matches `(display-mode: standalone)`.
 */
export const useIsBrowserFullscreen = (): boolean => {
  const [isFullscreen, setIsFullscreen] = useState(readIsFullscreen);

  useEffect(() => {
    const mediaQuery = window.matchMedia?.(FULLSCREEN_QUERY);
    if (!mediaQuery) {
      return;
    }

    const onChange = (event: MediaQueryListEvent) => {
      setIsFullscreen(event.matches);
    };

    // Re-read in case full-screen was toggled between the initial render and
    // this effect.
    setIsFullscreen(mediaQuery.matches);
    mediaQuery.addEventListener("change", onChange);

    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return isFullscreen;
};
